import React, { useEffect, useState } from "react";
import type { MenuProps } from "antd";
import {
  Button,
  Drawer,
  Dropdown,
  Flex,
  Grid,
  Menu,
  notification,
  Typography,
} from "antd";
import { LogoutOutlined, MenuOutlined } from "@ant-design/icons";
import { useAuth0 } from "@auth0/auth0-react";
import "./index.css";
import { ROUTES } from "../../../config/routes";
import { ReducerState } from "../../../types";
import { ConnectedProps, connect } from "react-redux";
import { ReactComponent as VectechTextIconSvg } from "../../../assets/icons/Vectech Text - Main v2.svg";
import { ReactComponent as UserSvg } from "../../../assets/icons/userIcon.svg";
import { ReactComponent as UKHealthSecurityAgencySvg } from "../../../assets/icons/UKHealthSecurityAgency.svg";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import OrgSelector from "./OrgSelector";
import { readSuperUser } from "../../../utils/activeOrg";

const { Title } = Typography;

const mapStateToProps = (state: ReducerState) => {
  return {
    notifications: state.notifications,
  };
};

const connector = connect(mapStateToProps, {});

type Props = ConnectedProps<typeof connector> & {
  // Extra props go here
};

const Navbar: React.FC<Props> = ({ notifications }) => {
  const { user, isAuthenticated, logout } = useAuth0();
  const [api, contextHolder] = notification.useNotification();
  const location = useLocation();
  // Below xl the full row of links no longer fits - labels wrap onto several
  // lines and on a phone break one letter per line - so it collapses into a
  // side drawer instead.
  const screens = Grid.useBreakpoint();
  const compact = !screens.xl;
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Following a link, or widening past the breakpoint, leaves nothing for an
  // open drawer to show.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, compact]);

  useEffect(() => {
    getNotify();
    // eslint-disable-next-line
  }, [notifications?.count]);

  const getNotify = () => {
    const notiArray = Object.values(notifications?.notifications);
    if (notiArray?.length > 0) {
      api[notiArray[notiArray?.length - 1].severity]({
        message: "Notification",
        description: notiArray[notiArray?.length - 1].message,
        duration: 5,
      });
    }
  };

  const logoutWithRedirect = () =>
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });

  const items: MenuProps["items"] = [
    {
      label: (
        <a
          href="/#"
          onClick={(e) => {
            e.preventDefault();
            logoutWithRedirect();
          }}
        >
          Logout
        </a>
      ),
      key: "1",
    },
  ];

  const reportIssueUrl =
    "https://grove-jumper-74b.notion.site/20e858760a4a8042859fe6703de82af2";

  const supportItems: MenuProps["items"] = [
    // {
    //   label: "Guide",
    //   key: "guide",
    //   disabled: true,
    // },
    // {
    //   label: "Help",
    //   key: "help",
    //   disabled: true,
    // },
    {
      label: (
        <a href={reportIssueUrl} target="_blank" rel="noopener noreferrer">
          Report an issue
        </a>
      ),
      key: "report-issue",
    },
  ];

  const headers = [
    { text: "Surveillance Activity", route: ROUTES.home },
    { text: "Image Catalog", route: ROUTES.image_catalog },
    { text: "Traps Admin", route: ROUTES.traps_and_locations },
    { text: "Alerts & Notifications", route: ROUTES.alerts_notifications },
    // Superusers only. The route itself is guarded and every endpoint behind it
    // is enforced server-side; hiding the link keeps it out of everyone else's
    // way rather than being the protection.
    ...(readSuperUser(user)
      ? [{ text: "Super User", route: ROUTES.superuser }]
      : []),
  ];

  const isActive = (route: string) =>
    route === "/"
      ? location.pathname === "/"
      : location.pathname.includes(route);

  const drawerItems: MenuProps["items"] = [
    ...headers.map((header) => ({
      key: header.route,
      label: <Link to={header.route}>{header.text}</Link>,
    })),
    {
      key: "support",
      label: "Support",
      children: [
        {
          key: "report-issue",
          label: (
            <a href={reportIssueUrl} target="_blank" rel="noopener noreferrer">
              Report an issue
            </a>
          ),
        },
      ],
    },
  ];

  const logo = (
    <Link to="/">
      <Title level={5}>
        <VectechTextIconSvg className="logo-header" />
      </Title>
    </Link>
  );

  const getUserIcon = () => {
    const org = user?.["https://mos-id.com/user_metadata"]?.organization;
    if (org?.startsWith("UKHSA-MEZE")) {
      return <UKHealthSecurityAgencySvg width={70} height={70} />;
    } else {
      return <UserSvg width={22} height={22} />;
    }
  };

  return (
    <>
      {contextHolder}
      {isAuthenticated && (
        <nav className={compact ? "compact" : ""}>
          {compact ? (
            <>
              <Flex align="center" gap={4}>
                <Button
                  type="text"
                  size="large"
                  icon={<MenuOutlined />}
                  aria-label="Open menu"
                  aria-expanded={drawerOpen}
                  onClick={() => setDrawerOpen(true)}
                />
                {logo}
              </Flex>
              <Drawer
                placement="left"
                width={300}
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                title={<VectechTextIconSvg className="drawer-logo" />}
                className="nav-drawer"
                footer={
                  <Flex vertical gap={12}>
                    {/* Left-aligned rather than stretched, and gone
                        entirely when there is only one organization. */}
                    <div className="flex empty:hidden">
                      <OrgSelector />
                    </div>
                    <Flex align="center" gap={8}>
                      <span className="flex">{getUserIcon()}</span>
                      <Title level={5} style={{ margin: 0 }}>
                        {user?.name}
                      </Title>
                    </Flex>
                    <Button
                      icon={<LogoutOutlined />}
                      onClick={logoutWithRedirect}
                    >
                      Logout
                    </Button>
                  </Flex>
                }
              >
                <Menu
                  mode="inline"
                  items={drawerItems}
                  selectedKeys={headers
                    .filter((header) => isActive(header.route))
                    .map((header) => header.route)}
                />
              </Drawer>
            </>
          ) : (
            <>
              <div className="nav-texts mt-6">
                {logo}
                {headers.map((header, index) => (
                  <Link
                    key={`nav-text-${index}`}
                    to={header.route}
                    className={isActive(header.route) ? "active" : ""}
                  >
                    <Title level={5}>{header.text}</Title>
                  </Link>
                ))}
                <Dropdown
                  menu={{ items: supportItems }}
                  trigger={["hover"]}
                  placement="bottom"
                >
                  <span className="support-link">
                    <Title level={5}>Support</Title>
                  </span>
                </Dropdown>
              </div>

              <ul>
                {/* Left of the user's name: the organization you are acting
                    as, then who you are. Renders nothing for anyone with a
                    single organization. */}
                <li>
                  <OrgSelector />
                </li>
                <li>
                  <Dropdown
                    menu={{ items }}
                    trigger={["click"]}
                    placement="bottomRight"
                  >
                    <Flex
                      align="center"
                      gap={8}
                      style={{ cursor: "pointer" }}
                      justify={"center"}
                    >
                      <span className="m-auto">{getUserIcon()}</span>
                      <Title level={5} style={{ margin: 0 }}>
                        {user?.name}
                      </Title>
                    </Flex>
                  </Dropdown>
                </li>
              </ul>
            </>
          )}
        </nav>
      )}
    </>
  );
};

export default connector(Navbar);
